import { Component, forwardRef, Input } from '@angular/core';
import { AsyncPipe, NgIf } from '@angular/common';
import {
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { UIThemeService } from '@services/UITheme/UITheme.service';
import {
  MatDatepicker,
  MatDatepickerInputEvent,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MyErrorStateMatcher } from '@validators/matcher';
import { Store } from '@ngrx/store';
import { IAppState } from '@myStore/app.state';
import { GlobalConstants } from '@globalConstants';
import { MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-date-picker',
  standalone: true,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    NgIf,
    MatTooltipModule,
  ],
  templateUrl: './date-picker.component.html',
  styleUrl: './date-picker.component.sass',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: GlobalConstants.DEFAULT_DATEPICKER_FORMAT,
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'ru-RU',
    },
  ],
})
export class DatePickerComponent {
  @Input() public getErrorCallback!: () => string | null;
  @Input() public valueDate!: () => Date | null;
  @Input() formGroup!: FormGroup;
  @Input() control!: FormControl;
  @Input() tooltipIfDisabled?: string;
  @Input() formControlName!: string;
  @Input() label!: string;
  @Input() requiredErrorMessage!: string;
  @Input() hint!: 'DD.MM.YYYY';
  @Input() prevDateDisabledKey = false;
  @Input() appearance = '';
  @Input() datePicker!: MatDatepicker<Date>;
  @Input() errorFromApi!: string;
  @Input() max!: boolean;
  @Input() min!: boolean;
  value: Date | null = null;

  // eslint-disable-next-line
  private onChange = (value: Date | null) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};
  public matcher = new MyErrorStateMatcher();

  constructor(
    public UIThemeService: UIThemeService,
    public _store: Store<IAppState>
  ) {}

  onDateChange(e: MatDatepickerInputEvent<Date>) {
    this.value = e.value;
    this.onChange(this.value);
  }

  writeValue(value: Date | null): void {
    this.value = value;
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  get formControlFunc() {
    return this.formGroup.get(this.formControlName);
  }

  prevDateDisabled(date: Date | null) {
    if (!date) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= today;
  }

  protected readonly FormControl = FormControl;
}
